import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook genérico para queries Supabase com estados loading/error/data e refetch.
 *
 * Uso:
 *   const { data, loading, error, refetch } = useSupabaseQuery(
 *     (sb) => sb.from('contatos').select('*').order('updated_at', { ascending: false }),
 *     [filterX, filterY] // deps que disparam novo fetch ao mudar
 *   );
 *
 * queryFn recebe o cliente supabase e deve retornar a query (thenable) — o hook
 * aguarda `.then()` e lê { data, error }.
 */
export function useSupabaseQuery(queryFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await queryFn(supabase);
      if (cancelledRef.current) return;
      if (result?.error) throw result.error;
      setData(result?.data ?? null);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err?.message || String(err));
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, deps);

  useEffect(() => {
    cancelledRef.current = false;
    refetch();
    return () => { cancelledRef.current = true; };
  }, [refetch]);

  return { data, loading, error, refetch };
}
